import React, { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const VehicleType = () => {
  const [loading, setLoading] = useState(true);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    image: null
  });
  const [imagePreview, setImagePreview] = useState('');
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;

  useEffect(() => {
    fetchVehicleTypes();
  }, []);

  const fetchVehicleTypes = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('vehicleTypeList');
      setVehicleTypes(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch vehicle types');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setFormData({ id: '', name: '', image: null });
    setImagePreview('');
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setFormData({ id: item._id, name: item.name, image: null });
    setImagePreview(item.image ? `${mediaUrl}${item.image}` : '');
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Please choose a smaller image less than 2MB');
        e.target.value = '';
        return;
      }
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = new FormData();
      if (formData.id) data.append('id', formData.id);
      data.append('name', formData.name);
      if (formData.image) data.append('image', formData.image);

      const response = await apiService.postFormData('updateVehicleType', data);
      if (response.success) {
        toast.error(response.message);
      } else {
        toast.success('Vehicle updated successfully');
        setShowModal(false);
        fetchVehicleTypes();
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await apiService.get(`deleteVehicleType/${deleteId}`);
      if (response.success) {
        toast.error(response.message);
      } else {
        toast.success('Vehicle type deleted successfully');
        setVehicleTypes(vehicleTypes.filter(item => item._id !== deleteId));
        setShowDeleteModal(false);
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#009ef7' }}></div>
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }

  return (
    <div className="p-0">
      <div className="bg-white rounded-lg shadow">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="text-xl font-bold text-gray-900">Vehicle Type</h3>
          <button
            onClick={handleAddNew}
            className="px-2 sm:px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center"
            style={{ backgroundColor: '#009ef7' }}
          >
            <i className="bi bi-plus text-lg mr-1"></i>
            Add New
          </button>
        </div>

        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-sm">
                  <th className="px-4 py-3 text-left font-semibold">Icon</th>
                  <th className="px-4 py-3 text-left font-semibold">Name</th>
                  <th className="px-4 py-3 text-center font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {vehicleTypes.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="px-4 py-8 text-center text-gray-500">
                      No vehicle types found
                    </td>
                  </tr>
                ) : (
                  vehicleTypes.map((item) => (
                    <tr key={item._id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <img
                          src={item.image ? `${mediaUrl}${item.image}` : '/placeholder.png'}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded"
                        />
                      </td>
                      <td className="px-4 py-3 text-gray-900">{item.name}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleEdit(item)}
                          className="inline-flex items-center justify-center w-8 h-8 mr-2 bg-green-50 text-green-600 rounded hover:bg-green-100"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(item._id)}
                          className="inline-flex items-center justify-center w-8 h-8 bg-red-50 text-red-600 rounded hover:bg-red-100"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Vehicle Type</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter Facility Name"
                  className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                  required
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icon <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div
                      className="w-36 h-36 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden"
                      style={{
                        backgroundImage: imagePreview ? `url(${imagePreview})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    >
                      {!imagePreview && <i className="bi bi-image text-4xl text-gray-400"></i>}
                    </div>
                    <label
                      className="absolute bottom-2 right-2 w-6 h-6 bg-white rounded-full shadow flex items-center justify-center cursor-pointer hover:bg-gray-50"
                      title="Change icon"
                    >
                      <i className="bi bi-pencil text-xs"></i>
                      <input
                        type="file"
                        accept=".png,.jpg,.jpeg"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Allowed file types: png, jpg, jpeg (Max 2MB)</p>
              </div>

              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 text-sm font-medium text-white rounded-lg"
                  style={{ backgroundColor: '#009ef7' }}
                >
                  {submitting ? 'Please wait...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Confirmation</h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700">Are you sure you want to delete this vehicle?</p>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleType;
